import React, { useState, useEffect, useMemo } from 'react';
import { CarOutlined, CheckOutlined, LeftOutlined, RightOutlined, ClockCircleOutlined, LoadingOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { DownOutlined } from '@ant-design/icons';
import { DatePicker, message, Input, Select } from 'antd';
import dayjs from 'dayjs';
import axios from '../../../../../utils/axiosConfig';
import { useAuth } from '../../../../../contexts/AuthContext';

const { RangePicker } = DatePicker;



const ConfigureStopLogic = ({ campaignData, onUpdate, onNext, isAdminCreating, targetUserId }) => {
  const { currentUser, token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [geotabDatabases, setGeotabDatabases] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  const vehiclesPerPage = 6;
  
  // Check if user is enterprise/agency
  const isEnterprise = currentUser?.role === 'agency' || currentUser?.role === 'enterprise';

  // Fetch vehicles - either from assignments (enterprise) or Geotab (client)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // For enterprise users, fetch assigned vehicles from our API
        if (isEnterprise && !isAdminCreating) {
          console.log('[ConfigureStopLogic] Enterprise mode - fetching assigned vehicles');
          
          // First fetch assignments, workspaces, and geotab accounts
          const [assignmentsRes, workspacesRes, accountsRes] = await Promise.all([
            axios.get('/api/vehicle-tracking/assignments', { headers }),
            axios.get('/api/vehicle-tracking/workspaces', { headers }),
            axios.get('/api/integrations/geotab/accounts', { headers })
          ]);
          
          const assignments = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : (assignmentsRes.data.items || []);
          const workspaces = Array.isArray(workspacesRes.data) ? workspacesRes.data : (workspacesRes.data.items || []);
          const accounts = Array.isArray(accountsRes.data) ? accountsRes.data : (accountsRes.data.items || []);
          const databases = accounts.map(a => a?.database).filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i);
          
          console.log('[ConfigureStopLogic] Assignments:', assignments.length, 'Workspaces:', workspaces.length, 'Databases:', databases);
          
          // Filter assignments by selected location if provided (use Number comparison)
          let filteredAssignments = assignments;
          if (campaignData.selectedLocations?.length > 0) {
            filteredAssignments = assignments.filter(a => 
              campaignData.selectedLocations.some(loc => Number(loc) === Number(a.workspace_id))
            );
          }
          console.log('[ConfigureStopLogic] Filtered assignments:', filteredAssignments.length);
          
          // If we have assignments, fetch device details
          let devices = [];
          if (databases.length > 0) {
            try {
              const devicesRes = await axios.get(`/api/integrations/geotab/devices?databases=${databases.join(',')}`, { headers });
              devices = Array.isArray(devicesRes.data?.devices) ? devicesRes.data.devices : [];
            } catch (devErr) {
              console.error('[ConfigureStopLogic] Error fetching devices:', devErr);
            }
          }
          
          // Map assignments to vehicles with device details
          const assignedVehicles = filteredAssignments.map(assignment => {
            const device = devices.find(d => d.id === assignment.geotab_device_id);
            const workspace = workspaces.find(w => Number(w.id) === Number(assignment.workspace_id));
            return {
              id: assignment.geotab_device_id,
              name: device?.name || `Vehicle ${assignment.geotab_device_id}`,
              location: workspace?.name || 'Unassigned'
            };
          });
          
          console.log('[ConfigureStopLogic] Assigned vehicles:', assignedVehicles.length);
          setVehicles(assignedVehicles);
          setGeotabDatabases(databases.length > 0 ? databases : ['assigned']);
          setLoading(false);
          return;
        }
        
        // For client users, fetch only vehicles assigned by superadmin
        console.log('[ConfigureStopLogic] Client mode - fetching assigned vehicles from vehicle management');
        
        try {
          // Fetch vehicles assigned to this client by superadmin
          const assignedVehiclesRes = await axios.get('/api/client-vehicle-assignments/my-vehicles', { headers });
          const assignedVehicles = Array.isArray(assignedVehiclesRes.data) ? assignedVehiclesRes.data : [];
          
          console.log('[ConfigureStopLogic] Assigned vehicles from management:', assignedVehicles.length);
          
          if (assignedVehicles.length === 0) {
            message.warning('No vehicles have been assigned to your account. Please contact your administrator.');
            setVehicles([]);
            setGeotabDatabases([]);
            setLoading(false);
            return;
          }
          
          // Get unique databases from assigned vehicles
          const databases = assignedVehicles
            .map(v => v.geotab_database)
            .filter(Boolean)
            .filter((v, i, arr) => arr.indexOf(v) === i);
          
          setGeotabDatabases(databases);
          
          // Fetch device details from Geotab for assigned vehicles only
          let devices = [];
          if (databases.length > 0) {
            try {
              const devicesRes = await axios.get(`/api/integrations/geotab/devices?databases=${databases.join(',')}`, { headers });
              devices = Array.isArray(devicesRes.data?.devices) ? devicesRes.data.devices : [];
            } catch (devErr) {
              console.error('[ConfigureStopLogic] Error fetching devices:', devErr);
            }
          }
          
          // Map assigned vehicles with device details
          const vehiclesWithDetails = assignedVehicles.map(assignment => {
            const device = devices.find(d => d.id === assignment.geotab_device_id);
            return {
              id: assignment.geotab_device_id,
              name: device?.name || assignment.vehicle_name || `Vehicle ${assignment.geotab_device_id}`,
              location: assignment.geotab_account_name || assignment.geotab_database || 'Unassigned'
            };
          });
          
          console.log('[ConfigureStopLogic] Vehicles with details:', vehiclesWithDetails.length);
          setVehicles(vehiclesWithDetails);
        } catch (assignErr) {
          console.error('[ConfigureStopLogic] Error fetching assigned vehicles:', assignErr);
          message.error('Failed to load assigned vehicles');
          setVehicles([]);
          setGeotabDatabases([]);
        }
        
        setLoading(false);
      } catch (e) {
        console.error('Error fetching data:', e);
        setGeotabDatabases([]);
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, token, isAdminCreating, targetUserId, isEnterprise, campaignData.selectedLocations]);

  const fetchVehicles = async () => {
    if (!geotabDatabases.length) {
      message.warning('No Geotab databases configured');
      return;
    }

    setLoadingVehicles(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const query = new URLSearchParams();
      query.set('databases', geotabDatabases.join(','));
      const { data } = await axios.get(`/api/integrations/geotab/devices?${query.toString()}`, { headers });
      const items = Array.isArray(data?.devices) ? data.devices : [];
      
      const transformedVehicles = items.map((device) => {
        // Try to get location from groups, comment, or use device name
        const locationName = device.groups?.[0]?.name || 
                            device.comment || 
                            device.licensePlate || 
                            device.serialNumber || 
                            'Unknown Location';
        
        return {
          id: device.id,
          name: device.name || `Vehicle ${device.id}`,
          location: locationName
        };
      });
      
      setVehicles(transformedVehicles);
      
      if (transformedVehicles.length > 0) {
        message.success(`Loaded ${transformedVehicles.length} vehicle(s) from Geotab`);
      } else {
        message.info('No vehicles found in Geotab');
      }
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      message.error('Failed to load vehicles from Geotab');
    } finally {
      setLoadingVehicles(false);
    }
  };

  // Get unique locations for filter
  const uniqueLocations = useMemo(() => {
    const locations = vehicles.map(v => v.location).filter(Boolean).filter(loc => loc !== 'N/A');
    return ['all', ...Array.from(new Set(locations))];
  }, [vehicles]);

  // Filter vehicles based on search and location filter
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(vehicle => {
      const matchesSearch = !searchQuery || 
        vehicle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vehicle.location && vehicle.location.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesLocation = filterLocation === 'all' || vehicle.location === filterLocation;
      
      return matchesSearch && matchesLocation;
    });
  }, [vehicles, searchQuery, filterLocation]);

  const totalPages = Math.ceil(filteredVehicles.length / vehiclesPerPage);
  const startIndex = (currentPage - 1) * vehiclesPerPage;
  const visibleVehicles = filteredVehicles.slice(startIndex, startIndex + vehiclesPerPage);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterLocation]);

  const toggleVehicle = (vehicleId) => {
    const currentSelected = campaignData.selectedVehicles || [];
    const isSelected = currentSelected.includes(vehicleId);
    if (isSelected) {
      onUpdate({ selectedVehicles: currentSelected.filter(id => id !== vehicleId) });
    } else {
      onUpdate({ selectedVehicles: [...currentSelected, vehicleId] });
    }
  };

  const selectAllVehicles = () => {
    const allIds = filteredVehicles.map(v => v.id);
    onUpdate({ selectedVehicles: allIds });
  };

  const unselectAllVehicles = () => {
    onUpdate({ selectedVehicles: [] });
  };

  const handleDateChange = (dates) => {
    onUpdate({ dateRange: dates });
  };


  const handleStopDurationChange = (type, value) => {
    const newDuration = { ...stopDuration };
    if (type === 'min') {
      newDuration.min = Math.min(value, stopDuration.max - 5);
    } else {
      newDuration.max = Math.max(value, stopDuration.min + 5);
    }
    onUpdate({ stopDuration: newDuration });
  };

  const handleContinue = () => {
    if (!campaignData.selectedVehicles || campaignData.selectedVehicles.length === 0) {
      message.warning('Please select at least one vehicle');
      return;
    }
    if (!campaignData.dateRange || campaignData.dateRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    onNext();
  };

  const selectedVehicles = campaignData.selectedVehicles || [];
  const stopDuration = campaignData.stopDuration || { min: 5, max: 720 };
  const hasDateRange = campaignData.dateRange && campaignData.dateRange.length === 2;

  // Show loading state
  if (loading) {
    return (
      <div className="step-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
          <p style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            {isEnterprise ? 'Loading assigned vehicles...' : 'Loading Geotab configuration...'}
          </p>
          <p style={{ color: '#8c8c8c', fontSize: 14 }}>
            {isEnterprise ? 'Fetching vehicles assigned to your locations' : 'Fetching your vehicles and account settings'}
          </p>
        </div>
      </div>
    );
  }
  
  // Show loading state while fetching vehicles
  if (loadingVehicles) {
    return (
      <div className="step-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: 'var(--ink)', marginBottom: 16 }} spin />
          <p style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Loading vehicles from Geotab...</p>
          <p style={{ color: '#8c8c8c', fontSize: 14 }}>This may take a moment</p>
        </div>
      </div>
    );
  }

  // Show initial state with Load Vehicles button (for client users) or no vehicles message (for enterprise)
  if (vehicles.length === 0 && !loadingVehicles) {
    // For enterprise users, show message that no vehicles are assigned
    if (isEnterprise) {
      return (
        <div className="step-container">
          <h1 className="step-title">Configure StopLogic™</h1>
          <p className="step-subtitle">Select vehicles, date range, stop duration, and targeting radius</p>
          
          <div style={{ 
            background: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: '12px', 
            padding: '40px', 
            textAlign: 'center',
            marginTop: '40px',
            maxWidth: '600px',
            margin: '40px auto'
          }}>
            <p style={{ color: '#d46b08', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No Vehicles Assigned</p>
            <p style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '24px' }}>
              No vehicles are assigned to the selected location(s). Please go to Vehicle Management to assign vehicles first.
            </p>
          </div>
        </div>
      );
    }
    
    // For client users, show Load Vehicles button
    return (
      <div className="step-container">
        <h1 className="step-title">Configure StopLogic™</h1>
        <p className="step-subtitle">Select vehicles, date range, stop duration, and targeting radius</p>
        
        <div style={{ 
          background: '#f0f5ff', 
          border: '1px solid #adc6ff', 
          borderRadius: '12px', 
          padding: '40px', 
          textAlign: 'center',
          marginTop: '40px',
          maxWidth: '600px',
          margin: '40px auto'
        }}>
          <p style={{ color: 'var(--ink)', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Load Your Vehicles</p>
          <p style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '24px' }}>Click the button below to fetch your vehicles from Geotab</p>
          <button 
            className="continue-btn" 
            onClick={fetchVehicles}
            disabled={loadingVehicles}
          >
            {loadingVehicles ? 'Loading...' : 'Load Vehicles from Geotab'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container">
      <h1 className="step-title">Configure StopLogic™</h1>
      <p className="step-subtitle">Select vehicles, date range, stop duration, and targeting radius</p>

      <div className="configure-container">
        {/* Left: Vehicles Selection */}
        <div className="vehicles-section">
          <div className="section-header">
            <span className="section-title">Select Vehicles ({filteredVehicles.length})</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                className="select-all-link" 
                onClick={fetchVehicles}
                disabled={loadingVehicles}
                style={{ marginRight: '8px' }}
              >
                {loadingVehicles ? <LoadingOutlined /> : '🔄'} Reload
              </button>
              <button className="select-all-link" onClick={selectAllVehicles}>
                Select All
              </button>
              <button className="select-all-link" onClick={unselectAllVehicles}>
                Unselect All
              </button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
            <Input
              placeholder="Search vehicles by name or location..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ flex: 1 }}
            />
            <Select
              value={filterLocation}
              onChange={setFilterLocation}
              style={{ width: 200 }}
              suffixIcon={<FilterOutlined />}
            >
              {uniqueLocations.map(loc => (
                <Select.Option key={loc} value={loc}>
                  {loc === 'all' ? 'All Locations' : loc}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="vehicles-grid">
            {visibleVehicles.map((vehicle) => {
              const isSelected = selectedVehicles.includes(vehicle.id);
              return (
                <div
                  key={vehicle.id}
                  className={`vehicle-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleVehicle(vehicle.id)}
                >
                  <div className="vehicle-icon">
                    <CarOutlined />
                  </div>
                  <div className="vehicle-info">
                    <div className="vehicle-name">{vehicle.name}</div>
                    <div className="vehicle-location">{vehicle.location}</div>
                  </div>
                  {isSelected && (
                    <div className="vehicle-check">
                      <CheckOutlined />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <LeftOutlined />
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <RightOutlined />
              </button>
            </div>
          )}
        </div>

        {/* Right: Configuration Panel */}
        <div className="config-section">
          <div className="config-panel">
            {/* Date Range */}
            <div className="config-item">
              <label className="config-label">Date Range</label>
              <RangePicker
                style={{ width: '100%', height: 44 }}
                value={campaignData.dateRange}
                onChange={handleDateChange}
                format="MMM D"
                placeholder={['Start Date', 'End Date']}
                defaultValue={[dayjs().subtract(7, 'day'), dayjs()]}
              />
            </div>

            {/* Stop Duration */}
            <div className="config-item">
              <label className="config-label">Stop Duration</label>
              <p className="config-sublabel">Filter stops by duration range</p>
              
              {/* Minimum Duration */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--ink-200)' }}>
                    <ClockCircleOutlined /> Min Duration
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>
                    {stopDuration.min} min
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="175"
                  step="5"
                  value={stopDuration.min}
                  onChange={(e) => handleStopDurationChange('min', parseInt(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Maximum Duration */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--ink-200)' }}>
                    <ClockCircleOutlined /> Max Duration
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>
                    {stopDuration.max >= 720 ? '12h+' : stopDuration.max >= 60 ? `${Math.floor(stopDuration.max / 60)}h ${stopDuration.max % 60}m` : `${stopDuration.max} min`}
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="720"
                  step="15"
                  value={stopDuration.max}
                  onChange={(e) => handleStopDurationChange('max', parseInt(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Duration Range Display */}
              <div style={{ 
                marginTop: '12px', 
                padding: '8px 12px', 
                background: '#f0f5ff', 
                borderRadius: '6px',
                fontSize: '13px',
                color: 'var(--ink)',
                textAlign: 'center'
              }}>
                Showing stops between {stopDuration.min} min and {stopDuration.max >= 720 ? '12h+' : stopDuration.max >= 60 ? `${Math.floor(stopDuration.max / 60)}h ${stopDuration.max % 60}m` : `${stopDuration.max} min`}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          <strong>{selectedVehicles.length}</strong> vehicles selected
          {hasDateRange && <span style={{ marginLeft: '12px' }}>• Date range selected</span>}
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default ConfigureStopLogic;
